/**
 * Category fields: the extra facts a product carries when one of its purposes
 * has them, so a buyer can narrow that shelf by what matters on it. Teaching
 * (the Education purpose) is the first set: year level, subject, kind of
 * resource, curriculum and format, the things teachers shop by. Built general
 * so the next niche adds a set here rather than a new mechanism.
 *
 * STORED as `categoryFields` on products/{id}: a map from a field's key to a
 * list of slugs from that field's closed list. Every field is optional.
 * firestore.rules checks only the map and its keys (categoryFieldsWithinBounds):
 * a full-size product save has no expression budget left for the lists.
 *
 * READ ONLY THROUGH categoryFieldsFor, which is what makes the lists closed:
 * it keeps only entries on this file's lists, at most each field's cap, so an
 * entry written straight to the database is never shown or filtered by. And
 * it keeps a set's fields only while the product's purposes include that
 * set's purpose: a seller who moves a product off Education does not leave it
 * under Year 3 in the marketplace, and moving it back brings the fields back.
 *
 * YEAR LEVELS ARE STORED BY AGE AND SHOWN BY COUNTRY. A UK Year 4, an
 * Australian Year 3 and a US Grade 3 are the same children (8 at the start of
 * the school year), so the stored slug is the US-style grade and each school
 * system names it in its own words. A visitor sees their own system's names,
 * picked from their country (schoolSystemForCountry), and can switch.
 */

/** The three school systems whose names the fields use. */
export type SchoolSystem = 'au' | 'uk' | 'us';

export const SCHOOL_SYSTEMS: readonly SchoolSystem[] = ['au', 'uk', 'us'];

export const SCHOOL_SYSTEM_LABELS: Readonly<Record<SchoolSystem, string>> = {
  au: 'Australia',
  uk: 'UK',
  us: 'US',
};

/**
 * The system a visitor from `country` (ISO 3166 alpha-2, '' when unknown)
 * reads year levels in. New Zealand and Ireland count their years as the UK
 * does (Year 13 is the last), so they share its names; everyone else gets
 * grades, the names most of the world's English-language schools use.
 */
export function schoolSystemForCountry(country: unknown): SchoolSystem {
  const code = typeof country === 'string' ? country.toUpperCase() : '';
  if (code === 'AU') return 'au';
  if (code === 'GB' || code === 'NZ' || code === 'IE' || code === 'JE' || code === 'GG' || code === 'IM') return 'uk';
  return 'us';
}

export function isSchoolSystem(value: unknown): value is SchoolSystem {
  return value === 'au' || value === 'uk' || value === 'us';
}

export type CategoryFieldOption = {
  /** The stored value, and the value in a marketplace URL. */
  slug: string;
  /** The name everywhere, unless `names` gives a school system its own. */
  label: string;
  names?: Partial<Record<SchoolSystem, string>>;
  /** The school system an option belongs to (a curriculum), listed first for its visitors. */
  system?: SchoolSystem;
};

export type CategoryFieldKey = 'grades' | 'subjects' | 'resourceKinds' | 'curricula' | 'formats';

export type CategoryField = {
  key: CategoryFieldKey;
  /** The field's name, unless `names` gives a school system its own. */
  label: string;
  names?: Partial<Record<SchoolSystem, string>>;
  /** Its marketplace URL parameter. */
  param: string;
  /** How many a product can carry. */
  max: number;
  options: readonly CategoryFieldOption[];
};

export type CategoryFieldSet = {
  /** The purpose (catalog-taxonomy.ts) whose products carry these fields. */
  purpose: string;
  fields: readonly CategoryField[];
};

/** A product's fields: each key holds the slugs picked, in list order. */
export type CategoryFieldValues = Partial<Record<CategoryFieldKey, string[]>>;

/** The year levels a single age covers, preschool to the last year of school. */
const SCHOOL_YEARS = 12;

/** The year level of a teaching resource made for no one age (TPT's Not Grade Specific). */
export const ANY_YEAR_LEVEL = 'any';

/** The year level of a resource teachers use themselves, not with students (TPT's Staff). */
export const FOR_TEACHERS = 'staff';

function gradeOptions(): CategoryFieldOption[] {
  const options: CategoryFieldOption[] = [
    { slug: 'prek', label: 'Pre-K', names: { au: 'Preschool', uk: 'Reception', us: 'Pre-K' } },
    { slug: 'k', label: 'Kindergarten', names: { au: 'Foundation', uk: 'Year 1', us: 'Kindergarten' } },
  ];
  for (let year = 1; year <= SCHOOL_YEARS; year++) {
    options.push({
      slug: `g${year}`,
      label: `Grade ${year}`,
      names: { au: `Year ${year}`, uk: `Year ${year + 1}`, us: `Grade ${year}` },
    });
  }
  options.push(
    { slug: 'higher-ed', label: 'Higher education' },
    { slug: 'adult', label: 'Adult education' },
    // So every teaching resource has a year level to give, including one made
    // for any year and a teacher's own planner (owner, 2026-10-28). Not on the
    // ladder: a summary names them on their own.
    { slug: ANY_YEAR_LEVEL, label: 'Not grade specific', names: { au: 'Not year specific', uk: 'Not year specific' } },
    { slug: FOR_TEACHERS, label: 'For teachers' },
  );
  return options;
}

/** The school years in age order: the ladder a run of years is read along. */
const GRADE_LADDER: readonly string[] = ['prek', 'k', ...Array.from({ length: SCHOOL_YEARS }, (_, i) => `g${i + 1}`)];

export const TEACHING_FIELDS: readonly CategoryField[] = [
  {
    key: 'grades',
    label: 'Grade level',
    names: { au: 'Year level', uk: 'Year group', us: 'Grade level' },
    param: 'grade',
    max: 18,
    options: gradeOptions(),
  },
  {
    key: 'subjects',
    label: 'Subject',
    param: 'subject',
    max: 4,
    options: [
      { slug: 'english', label: 'English', names: { us: 'English language arts' } },
      { slug: 'maths', label: 'Maths', names: { us: 'Math' } },
      { slug: 'science', label: 'Science' },
      { slug: 'humanities', label: 'Humanities', names: { au: 'Humanities (HASS)', uk: 'History and geography', us: 'Social studies' } },
      { slug: 'languages', label: 'Languages', names: { uk: 'Modern languages', us: 'World languages' } },
      { slug: 'arts', label: 'The arts' },
      { slug: 'music', label: 'Music' },
      { slug: 'health-pe', label: 'Health and PE', names: { uk: 'PE and health' } },
      { slug: 'technology', label: 'Technology', names: { au: 'Technologies', uk: 'Computing and DT' } },
      { slug: 'wellbeing', label: 'Wellbeing', names: { uk: 'PSHE and wellbeing', us: 'Social-emotional learning' } },
      { slug: 'inclusion', label: 'Special education', names: { au: 'Inclusive education', uk: 'SEND' } },
      { slug: 'eal', label: 'ESL and ELL', names: { au: 'EAL/D', uk: 'EAL' } },
      { slug: 'religion', label: 'Religious education', names: { uk: 'RE' } },
      { slug: 'business-economics', label: 'Business and economics' },
      { slug: 'any', label: 'Any subject' },
    ],
  },
  {
    key: 'resourceKinds',
    label: 'Resource type',
    param: 'resource',
    max: 3,
    options: [
      { slug: 'worksheets', label: 'Worksheets' },
      { slug: 'task-cards', label: 'Task cards' },
      { slug: 'lesson-plans', label: 'Lesson plans' },
      { slug: 'unit-plans', label: 'Unit plans', names: { au: 'Units of work', uk: 'Schemes of work' } },
      { slug: 'sub-plans', label: 'Sub plans', names: { au: 'Relief lessons', uk: 'Cover lessons' } },
      { slug: 'assessments', label: 'Assessments and tests' },
      { slug: 'activities', label: 'Activities and games' },
      { slug: 'centres', label: 'Centres and stations', names: { au: 'Centres and rotations', us: 'Centers and stations' } },
      { slug: 'slides', label: 'Slides and presentations' },
      { slug: 'reading', label: 'Reading passages and readers' },
      { slug: 'flashcards', label: 'Flashcards' },
      { slug: 'graphic-organisers', label: 'Graphic organisers', names: { us: 'Graphic organizers' } },
      { slug: 'notes', label: 'Notes and study guides' },
      { slug: 'homework', label: 'Homework' },
      { slug: 'projects', label: 'Projects' },
      { slug: 'digital-activities', label: 'Digital activities' },
      { slug: 'decor', label: 'Classroom displays', names: { us: 'Classroom decor' } },
      { slug: 'teacher-planning', label: 'Teacher planners and admin' },
    ],
  },
  {
    key: 'curricula',
    label: 'Curriculum',
    param: 'curriculum',
    max: 4,
    options: [
      { slug: 'australian-curriculum', label: 'Australian Curriculum', system: 'au' },
      { slug: 'nsw-syllabus', label: 'NSW syllabus', system: 'au' },
      { slug: 'victorian-curriculum', label: 'Victorian Curriculum', system: 'au' },
      { slug: 'national-curriculum', label: 'National Curriculum (England)', system: 'uk' },
      { slug: 'eyfs', label: 'EYFS (England)', system: 'uk' },
      { slug: 'curriculum-for-excellence', label: 'Curriculum for Excellence (Scotland)', system: 'uk' },
      { slug: 'nz-curriculum', label: 'New Zealand Curriculum', system: 'uk' },
      { slug: 'common-core', label: 'Common Core', system: 'us' },
      { slug: 'ngss', label: 'NGSS', system: 'us' },
      { slug: 'teks', label: 'TEKS (Texas)', system: 'us' },
      { slug: 'ib', label: 'International Baccalaureate' },
      { slug: 'cambridge', label: 'Cambridge International' },
    ],
  },
  {
    key: 'formats',
    label: 'Format',
    param: 'format',
    max: 6,
    options: [
      { slug: 'printable-pdf', label: 'Printable PDF' },
      { slug: 'fillable-pdf', label: 'Fillable PDF' },
      { slug: 'google-slides', label: 'Google Slides' },
      { slug: 'google-docs', label: 'Google Docs' },
      { slug: 'google-sheets', label: 'Google Sheets' },
      { slug: 'google-forms', label: 'Google Forms' },
      { slug: 'powerpoint', label: 'PowerPoint' },
      { slug: 'word', label: 'Word' },
      { slug: 'canva', label: 'Canva' },
      { slug: 'boom-cards', label: 'Boom Cards' },
      { slug: 'seesaw', label: 'Seesaw' },
      { slug: 'video', label: 'Video' },
      { slug: 'audio', label: 'Audio' },
      { slug: 'images', label: 'Image files' },
    ],
  },
];

/** The Education purpose's slug (catalog-taxonomy.ts), whose products carry the teaching fields. */
export const TEACHING_PURPOSE = 'education';

/** The teaching set: what a product with Education as a purpose carries. */
export const TEACHING_FIELD_SET: CategoryFieldSet = { purpose: TEACHING_PURPOSE, fields: TEACHING_FIELDS };

export const CATEGORY_FIELD_SETS: readonly CategoryFieldSet[] = [TEACHING_FIELD_SET];

/** Whether a product with these purposes is a teaching resource, so the form shows the classroom details. */
export function isTeachingProduct(purposes: readonly (string | null | undefined)[]): boolean {
  return purposes.includes(TEACHING_PURPOSE);
}

/** Every field of every set, by key: the keys are unique across sets. */
const FIELD_BY_KEY: ReadonlyMap<CategoryFieldKey, CategoryField> = new Map(
  CATEGORY_FIELD_SETS.flatMap((set) => set.fields.map((field) => [field.key, field] as const)),
);

export function categoryField(key: CategoryFieldKey): CategoryField {
  return FIELD_BY_KEY.get(key)!;
}

/** Every field, in set and list order. */
export const CATEGORY_FIELDS: readonly CategoryField[] = CATEGORY_FIELD_SETS.flatMap((set) => set.fields);

/** The set a product with these purposes carries, or null. */
export function categoryFieldSetFor(purposes: readonly (string | null | undefined)[]): CategoryFieldSet | null {
  return CATEGORY_FIELD_SETS.find((set) => purposes.includes(set.purpose)) ?? null;
}

const EMPTY_VALUES: CategoryFieldValues = Object.freeze({}) as CategoryFieldValues;

/**
 * `raw` reduced to what `set` allows: known keys, known slugs, no repeats, in
 * list order, at most each field's `max`. Fields left empty are left out, so
 * a product with none is `{}`.
 */
export function normalizeCategoryFields(raw: unknown, set: CategoryFieldSet): CategoryFieldValues {
  if (!raw || typeof raw !== 'object') return EMPTY_VALUES;
  const out: CategoryFieldValues = {};
  for (const field of set.fields) {
    const value = (raw as Record<string, unknown>)[field.key];
    if (!Array.isArray(value)) continue;
    const picked = new Set(value.filter((v): v is string => typeof v === 'string'));
    const slugs = field.options.map((option) => option.slug).filter((slug) => picked.has(slug)).slice(0, field.max);
    if (slugs.length > 0) out[field.key] = slugs;
  }
  return out;
}

/**
 * A product's category fields as every reader sees them: the stored map,
 * normalised, for the set its purposes switch on, and nothing when none does.
 * `purposes` is effectiveTaxonomyOf's, main first.
 */
export function categoryFieldsFor(raw: unknown, purposes: readonly string[]): CategoryFieldValues {
  const set = categoryFieldSetFor(purposes);
  return set ? normalizeCategoryFields(raw, set) : EMPTY_VALUES;
}

/**
 * Whether stored fields hold a year level: what makes a listing a teaching
 * resource on every browse surface (effectiveTaxonomyOf in product-filters.ts),
 * so a guide for adults filed under Teaching stays off its shelf (owner,
 * 2026-10-27). Read off the raw map, because the purposes it decides are what
 * categoryFieldsFor needs. Adult and higher education count: they are taught.
 * Not grade specific and For teachers count too, which is what lets the
 * launch check ask every teaching resource for one (teachingYearLevelMissing).
 */
export function hasYearLevel(raw: unknown): boolean {
  return (normalizeCategoryFields(raw, TEACHING_FIELD_SET).grades?.length ?? 0) > 0;
}

/** A listing as the launch check reads it: the seller's purposes and fields, and its status once saved. */
export type TeachingListing = {
  purpose?: unknown;
  secondaryPurpose?: unknown;
  categoryFields?: unknown;
  status?: unknown;
};

/**
 * Whether a listing its seller put under Teaching has no year level, which
 * holds its launch back (the finalize dialog's yearLevel issue, and
 * bulkLaunchProblems in bulk-import-finish.ts). Without one the shelf leaves
 * it off (hasYearLevel), so the seller is told at launch rather than finding
 * it missing later (owner, 2026-10-28).
 *
 * `saved` is the listing as stored, for an edit. One already on sale under
 * Teaching with no year level is let through, so its seller can still save
 * other changes. Those were picked while the purpose read "Education", before
 * 2026-10-27, mostly on guides for adults: holding their saves would push
 * their sellers onto Not grade specific just to get past, and onto the shelf.
 */
export function teachingYearLevelMissing(current: TeachingListing, saved?: TeachingListing | null): boolean {
  const missing = (listing: TeachingListing) => (
    (listing.purpose === TEACHING_PURPOSE || listing.secondaryPurpose === TEACHING_PURPOSE)
    && !hasYearLevel(listing.categoryFields)
  );
  if (!missing(current)) return false;
  return !(saved && saved.status !== 'draft' && missing(saved));
}

/** Whether any field holds a value. */
export function hasCategoryFields(values: CategoryFieldValues | null | undefined): boolean {
  return !!values && Object.values(values).some((list) => (list?.length ?? 0) > 0);
}

/**
 * Which of a field's options a product's picks answer: the picks themselves,
 * and for the year levels every age once Not grade specific is among them (a
 * resource for any year is one for Year 3, owner 2026-10-28). For teachers
 * stays its own: a teacher's planner is not a Year 3 resource.
 */
function answers(field: CategoryField, have: readonly string[]): (slug: string) => boolean {
  if (field.key === 'grades' && have.includes(ANY_YEAR_LEVEL)) {
    return (slug) => slug !== FOR_TEACHERS || have.includes(FOR_TEACHERS);
  }
  return (slug) => have.includes(slug);
}

/**
 * Whether a product's fields answer a filter: within a field any picked value
 * will do (a product is for Year 3 OR Year 4), and every field picked must be
 * answered (Year 3 AND Maths).
 */
export function matchesCategoryFields(values: CategoryFieldValues, wanted: CategoryFieldValues | null | undefined): boolean {
  if (!wanted) return true;
  for (const field of CATEGORY_FIELDS) {
    const picks = wanted[field.key];
    if (!picks || picks.length === 0) continue;
    const have = values[field.key];
    if (!have || !picks.some(answers(field, have))) return false;
  }
  return true;
}

/** Two filters asking for the same thing, whatever order their picks are in. */
export function sameCategoryFields(a: CategoryFieldValues | null | undefined, b: CategoryFieldValues | null | undefined): boolean {
  for (const field of CATEGORY_FIELDS) {
    const left = a?.[field.key] ?? [];
    const right = b?.[field.key] ?? [];
    if (left.length !== right.length || !left.every((slug) => right.includes(slug))) return false;
  }
  return true;
}

/** The field's name in a school system. */
export function fieldName(field: CategoryField, system: SchoolSystem): string {
  return field.names?.[system] ?? field.label;
}

/** An option's name in a school system, or the slug itself for one the list no longer has. */
export function optionName(field: CategoryField, slug: string, system: SchoolSystem): string {
  const option = field.options.find((o) => o.slug === slug);
  if (!option) return slug;
  return option.names?.[system] ?? option.label;
}

/**
 * A field's options in the order a visitor reads them: list order, except
 * that options belonging to a school system (the curricula) put the visitor's
 * own first, then the ones belonging to none, then the rest.
 */
export function optionsInOrder(field: CategoryField, system: SchoolSystem): CategoryFieldOption[] {
  const rank = (option: CategoryFieldOption) => (option.system === system ? 0 : option.system ? 2 : 1);
  return field.options
    .map((option, index) => ({ option, index }))
    .sort((a, b) => rank(a.option) - rank(b.option) || a.index - b.index)
    .map(({ option }) => option);
}

/**
 * Year levels as a reader says them: a run of consecutive school years as
 * "Year 3 to Year 6", single years and the levels after school on their own,
 * joined with commas. Never a dash, so screen readers and the house style
 * agree.
 */
export function gradeSummary(grades: readonly string[], system: SchoolSystem): string {
  const field = categoryField('grades');
  const name = (slug: string) => optionName(field, slug, system);
  const onLadder = GRADE_LADDER.filter((slug) => grades.includes(slug));
  const parts: string[] = [];
  let runStart = -1;
  let previous = -2;
  const closeRun = () => {
    if (runStart < 0) return;
    const first = GRADE_LADDER[runStart];
    const last = GRADE_LADDER[previous];
    parts.push(first === last ? name(first) : `${name(first)} to ${name(last)}`);
  };
  for (const slug of onLadder) {
    const at = GRADE_LADDER.indexOf(slug);
    if (at !== previous + 1) {
      closeRun();
      runStart = at;
    }
    previous = at;
  }
  closeRun();
  for (const option of field.options) {
    if (!GRADE_LADDER.includes(option.slug) && grades.includes(option.slug)) parts.push(name(option.slug));
  }
  return parts.join(', ');
}

/** A field's values as one line: the year levels as runs, anything else listed. */
export function fieldValueSummary(field: CategoryField, slugs: readonly string[], system: SchoolSystem): string {
  if (field.key === 'grades') return gradeSummary(slugs, system);
  return slugs.map((slug) => optionName(field, slug, system)).join(', ');
}

const FORMAT_BY_EXTENSION: Readonly<Record<string, string>> = {
  pdf: 'printable-pdf',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  pps: 'powerpoint',
  ppsx: 'powerpoint',
  doc: 'word',
  docx: 'word',
  mp4: 'video',
  mov: 'video',
  m4v: 'video',
  webm: 'video',
  mp3: 'audio',
  wav: 'audio',
  m4a: 'audio',
  aac: 'audio',
  ogg: 'audio',
  png: 'images',
  jpg: 'images',
  jpeg: 'images',
  gif: 'images',
  svg: 'images',
  webp: 'images',
};

/** Where an access link opens, as a format. Matched on the host and the start of the path. */
const FORMAT_BY_LINK: ReadonlyArray<{ host: RegExp; path?: RegExp; format: string }> = [
  { host: /^docs\.google\.com$/, path: /^\/presentation\//, format: 'google-slides' },
  { host: /^docs\.google\.com$/, path: /^\/document\//, format: 'google-docs' },
  { host: /^docs\.google\.com$/, path: /^\/spreadsheets\//, format: 'google-sheets' },
  { host: /^docs\.google\.com$/, path: /^\/forms\//, format: 'google-forms' },
  { host: /^forms\.gle$/, format: 'google-forms' },
  { host: /(^|\.)canva\.com$/, format: 'canva' },
  { host: /(^|\.)boomlearning\.com$/, format: 'boom-cards' },
  { host: /(^|\.)seesaw\.me$/, format: 'seesaw' },
];

/**
 * The formats a product's files and access links show it comes in, for the
 * listing form to offer with one click. Only offered, never saved unasked: a
 * PDF can be a printable or a fillable one, and only the seller knows which.
 */
export function suggestedFormats(input: { fileNames: readonly string[]; links: readonly string[] }): string[] {
  const found = new Set<string>();
  for (const name of input.fileNames) {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    const format = FORMAT_BY_EXTENSION[ext];
    if (format) found.add(format);
  }
  for (const link of input.links) {
    let url: URL;
    try {
      url = new URL(link.trim());
    } catch {
      continue;
    }
    const host = url.hostname.toLowerCase();
    const match = FORMAT_BY_LINK.find((rule) => rule.host.test(host) && (!rule.path || rule.path.test(url.pathname)));
    if (match) found.add(match.format);
  }
  return categoryField('formats').options.map((option) => option.slug).filter((slug) => found.has(slug));
}

/**
 * The fields as a marketplace query: one parameter per field, its slugs
 * comma-joined. Read back by categoryFieldsFromQuery, which drops anything
 * not on a list, so a hand-typed address cannot ask for an entry that is not.
 */
export function categoryFieldsQuery(values: CategoryFieldValues | null | undefined): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const field of CATEGORY_FIELDS) {
    const slugs = values?.[field.key];
    if (slugs && slugs.length > 0) out.push([field.param, slugs.join(',')]);
  }
  return out;
}

export function categoryFieldsFromQuery(read: (key: string) => string | null): CategoryFieldValues {
  const out: CategoryFieldValues = {};
  for (const field of CATEGORY_FIELDS) {
    const raw = read(field.param);
    if (!raw) continue;
    const wanted = new Set(raw.split(',').map((slug) => slug.trim()).filter(Boolean));
    const slugs = field.options.map((option) => option.slug).filter((slug) => wanted.has(slug));
    if (slugs.length > 0) out[field.key] = slugs;
  }
  return out;
}

/**
 * Each field's options with how many of `values` (one entry per product)
 * answer it, which is how many picking it would show: a Not grade specific
 * product counts under every year (matchesCategoryFields).
 */
export function categoryFieldFacets(
  field: CategoryField,
  values: readonly CategoryFieldValues[],
): Array<{ slug: string; count: number }> {
  const counts = new Map<string, number>();
  for (const product of values) {
    const have = product[field.key];
    if (!have || have.length === 0) continue;
    const answered = answers(field, have);
    for (const option of field.options) {
      if (answered(option.slug)) counts.set(option.slug, (counts.get(option.slug) ?? 0) + 1);
    }
  }
  return field.options.map((option) => ({ slug: option.slug, count: counts.get(option.slug) ?? 0 }));
}
